import { useState } from "react";
import { ImageLightbox } from "./ImageLightbox";

export interface CardPhoto {
  readonly url: string | null;
  readonly caption: string;
  /** Said in the frame when there is no photo. */
  readonly missing?: string;
}

/**
 * A card's page from 1024 px: its photos in a column of their own, 160 px each
 * side by side, beside the form, with the header over both (`PHOTOS_SPAN`). The
 * page is `PAGE_WIDE` that wide; the bottom bar follows the form's column.
 */
export const PHOTOS_BESIDE = "lg:grid lg:grid-cols-[20.75rem_minmax(0,1fr)] lg:items-start lg:gap-x-8";
export const PHOTOS_SPAN = "lg:col-span-2";
export const PAGE_WIDE = "lg:max-w-[1064px]";

/**
 * Two photos side by side, card-shaped, each opening the lightbox over every
 * photo there is. The review screen puts the scan beside what it was identified
 * as, so a parallel is settled by looking rather than by reading a name; the
 * card page shows its front and back.
 */
export function CardPhotos({ shown, gallery }: { shown: readonly CardPhoto[]; gallery: readonly CardPhoto[] }) {
  const [start, setStart] = useState<number | null>(null);
  const withPhoto = gallery.filter((photo): photo is CardPhoto & { url: string } => !!photo.url);

  const open = (photo: CardPhoto) => {
    const index = withPhoto.findIndex((p) => p.url === photo.url && p.caption === photo.caption);
    setStart(Math.max(0, index));
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {shown.map((photo) => (
          <figure key={photo.caption} className="min-w-0">
            {photo.url ? (
              <button
                type="button"
                onClick={() => open(photo)}
                aria-label={`Enlarge: ${photo.caption}`}
                className="block aspect-[5/7] w-full overflow-hidden rounded-row bg-surface-strong"
              >
                <img src={photo.url} alt="" className="h-full w-full object-contain" />
              </button>
            ) : (
              <div className="flex aspect-[5/7] w-full items-center justify-center rounded-row bg-surface-strong text-xs text-faint">
                {photo.missing ?? "No photo"}
              </div>
            )}
            <figcaption className="mt-1.5 truncate text-center text-[11px] text-muted">{photo.caption}</figcaption>
          </figure>
        ))}
      </div>
      {start !== null && withPhoto.length > 0 && (
        <ImageLightbox urls={withPhoto.map((p) => p.url)} startIndex={start} onClose={() => setStart(null)} />
      )}
    </>
  );
}
